import React, { FC, useEffect, useRef, useState } from "react";
import { HiDotsHorizontal, HiDotsVertical } from "react-icons/hi";
import { splitStringByComma } from "../../utils/splitStringByComma";
import { calculateHoursBetweenDates } from "../../utils/hoursBetweenDates";
import { AddRentalState, RentalType, Rentals } from "../../types";
import { ipfsGatewayImage } from "../../utils/ipfsGatewayImage";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useMultistepForm } from "../../hooks/useMultistepForm";
import Parked from "../../components/endTrip/Parked";
import Refund from "../../components/endTrip/Refund";
import Reason from "../../components/endTrip/Reason";
import Feedback from "../../components/endTrip/Feedback";
import { CiWarning } from "react-icons/ci";
import Warning from "../endTrip/Warning";
import {
  CREATE_CAR_RATING,
  CREATE_CANCELLED_RENTAL,
} from "../../apollo/operations";
import { useMutation } from "@apollo/client";
import Loading from "../loading/Loading";
import { FaStar } from "react-icons/fa6";
import ReviewCategory from "../endTrip/ReviewCategory";
import { Tick } from "../../assets/images";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../utils/showToast";
import { setCancelRental } from "../../redux/slices/rentalSlice";
import { format } from "date-fns";
import RentalChat from "../rentalChat/RentalChat";
interface RentalCardProps {
  rental: RentalType;
  index: number;
  refetch?: () => void;
}
const RentalCard: FC<RentalCardProps> = ({ index, rental, refetch }) => {
  const [isHover, setIsHover] = useState<boolean>(false);
  const [showEndTripModal, setShowEndTripModal] = useState<boolean>(false);
  const [showReview, setShowReview] = useState<boolean>(false);
  const [isReviewSubmitted, setIsReviewSubmitted] = useState<boolean>(false);
  const [cleanliness, setCleanliness] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [maintenance, setMaintenance] = useState(0);
  const [overallRating, setOverallRating] = useState(0);
  const [reviewText, setReviewText] = useState<string>("");

  const dispatch = useDispatch();

  const [createCarRating, { loading: createCarRatingLoading }] = useMutation(
    CREATE_CAR_RATING,
    {
      onCompleted(data) {
        // console.log(data);
        setIsReviewSubmitted(true);
      },
      onError(error) {
        console.log(error);
      },
    }
  );

  const optionsRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(optionsRef, () => {
    setIsHover(false);
  });

  function handleNext() {
    next();
  }

  const [createCancelledRental, { loading, data }] = useMutation(
    CREATE_CANCELLED_RENTAL,
    {
      onCompleted: (data) => {
        // console.log(data);
        dispatch(
          setCancelRental({
            id: "",
            reason: "",
          })
        );
        setShowEndTripModal(false);
        if (refetch) {
          refetch();
        }
        showToast("Trip cancelled successfully", "success");
      },
      onError: (error) => {
        // console.log(error);
      },
    }
  );

  const { step, isLastStep, next, prev, completed } = useMultistepForm([
    <Warning
      handleNext={handleNext}
      setShowEndTripModal={setShowEndTripModal}
      rental={rental}
    />,
    // <Parked handleNext={handleNext} />,
    <Refund handleNext={handleNext} />,
    <Reason handleNext={handleNext} />,
    <Feedback handleNext={handleNext} />,
  ]);

  const user = useSelector((state: any) => state.user.userDetails);
  const navigate = useNavigate();

  const cancelRental = useSelector(
    (state: any) => state.addRental.cancelRental
  );

  // if (cancelRental) {
  //   console.log(cancelRental);
  // }

  useEffect(() => {
    if (
      completed &&
      !data &&
      rental.rentalStatus === "Booked" &&
      cancelRental?.id &&
      cancelRental?.reason
    ) {
      createCancelledRental({
        variables: {
          data: {
            cancelRequestTime: new Date().toISOString(),
            reason: cancelRental.reason,
            rental: {
              id: cancelRental.id,
            },
          },
        },
      });
    }
  }, [
    completed,
    data,
    cancelRental,
    createCancelledRental,
    rental.rentalStatus,
  ]);

  const calculateOverallRating = () => {
    const total = cleanliness + accuracy + communication + maintenance;
    setOverallRating(total / 4);
  };

  useEffect(() => {
    calculateOverallRating();
  }, [cleanliness, accuracy, communication, maintenance]);

  if (loading || createCarRatingLoading)
    return <Loading loading={loading || createCarRatingLoading} />;

  return (
    <div
      className="flex w-[320px] shadow-2xl flex-grow max-w-[350px] p-5 pt-0 gap-5 flex-col bg-white border-none rounded-lg "
      key={index}
    >
      <div className="border-b  border-greyText text-[#656575] font-Inter font-medium text-lg flex justify-between items-center ">
        {/* #{rental?.id.slice(0, 9)}...{" "} */}
        <RentalChat
          title={"Lessee"}
          lessorId={rental.car.lessor.id}
          lesseeId={rental.lessee.id}
          rentalID={rental.id}
          className=" w-fit border-none"
        />
        <span
          className="cursor-pointer relative p-5"
          onClick={() => {
            setIsHover(!isHover);
          }}
        >
          {" "}
          {isHover ? (
            <HiDotsVertical size={20} />
          ) : (
            <HiDotsHorizontal size={20} />
          )}
          {isHover && (
            <div
              ref={optionsRef}
              className="absolute w-[150px] top-0 left-10 bg-white shadow-lg rounded-lg p-2"
            >
              <ul className="flex flex-col gap-2 p-3">
                <li
                  onClick={() => setShowReview(!showReview)}
                  className=" text-[#535356] hover:text-[#ff276a] font-Inter text-sm font-normal "
                >
                  Rate Trip
                </li>
                {rental.rentalStatus === "Booked" && (
                  <li
                    onClick={() => setShowEndTripModal(!showEndTripModal)}
                    className="text-[#535356] hover:text-[#ff276a] font-Inter text-sm font-normal "
                  >
                    Cancel trip
                  </li>
                )}
                {/* <li className=" text-[#535356] hover:text-[#ff276a] font-Inter text-sm font-normal ">
                  Edit Ride
                </li> */}
                {/* <li className=" text-[#535356] hover:text-[#ff276a] font-Inter text-sm font-normal ">
                  Share Vehicle
                </li> */}
                {/* <li className=" text-[#535356] hover:text-[#ff276a] font-Inter text-sm font-normal ">
                  Share Trip
                </li> */}
              </ul>
            </div>
          )}
        </span>
      </div>
      <div className="flex w-[100%] gap-3 items-center  border rounded-xl py-2 px-2 rentals-center">
        {/* <img src={rental.userImg} alt="" className="h-[37px]" /> */}
        <p className="w-[50px] h-[50px] border rounded-full p-3 flex items-center justify-center bg-midGreen text-white ">
          {rental?.car?.lessor?.firstName?.slice(0, 1)?.toUpperCase()}
          {rental?.car?.lessor?.lastName?.slice(0, 1)?.toUpperCase()}
        </p>
        <p className="flex flex-col text-[#525256] gap-1 font-Inter text-sm font-medium ">
          {rental?.car?.lessor?.firstName} {rental?.car?.lessor?.lastName}{" "}
        </p>
      </div>
      <div className="div flex gap-3 items-start  ">
        <img
          src={ipfsGatewayImage(
            splitStringByComma(rental?.car?.carImages[0]?.imageUrl)[0]
          )}
          alt=""
          className="object-contain rounded-md w-[25%] "
        />
        <div className="flex flex-col w-full flex-1 gap-3 items-start">
          <p className=" text-greyText uppercase whitespace-nowrap font-Inter font-normal text-xs ">
            Booking Info
          </p>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-[10px]">Total</span>
            <p className=" flex flex-col gap-1 text-[#656575] font-Inter text-[11px] font-semibold  ">
              ${rental?.escrows?.depositAmount}{" "}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-[10px]">Pickup Time</span>
            <p className=" flex flex-col gap-1 text-[#656575] font-Inter text-[11px] font-semibold  ">
              {format(new Date(rental?.rentalStartDatetime), "hh:mm a")}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-[10px]">Return Time</span>
            <p className=" flex flex-col gap-1 text-[#656575] font-Inter text-[11px] font-semibold  ">
              {format(new Date(rental?.rentalEndDatetime), "hh:mm a")}
            </p>
          </div>
        </div>
        <div className="flex flex-col w-[30%] gap-3 items-start">
          <p className=" text-greyText uppercase font-Inter font-normal text-xs ">
            {rental?.rentalStatus}
          </p>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-[10px]">Duration</span>
            <p className=" flex flex-col gap-1 text-[11px] text-[#656575] font-Inter font-medium  ">
              {calculateHoursBetweenDates(
                new Date(rental?.rentalStartDatetime),
                new Date(rental?.rentalEndDatetime)
              ) + " hours"}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-[10px]">Pickup Date</span>
            <p className=" flex flex-col gap-1 text-[11px] text-[#656575] font-Inter  font-medium  ">
              {format(new Date(rental?.rentalStartDatetime), "dd-MMM-yyyy")}
            </p>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-medium text-[10px]">Return Time</span>
            <p className=" flex flex-col gap-1 text-[11px] text-[#656575] font-Inter  font-medium  ">
              {format(new Date(rental?.rentalEndDatetime), "dd-MMM-yyyy")}
            </p>
          </div>
        </div>
        {/* <div className="flex flex-col w-[30%] h-full px-3 py-3 justify-between border rounded-xl">
          <text- className=" flex flex-col gap-1 text-[#656575] font-Inter text-xs font-bold  ">
            {calculateHoursBetweenDates(
              new Date(rental?.rentalStartDatetime),
              new Date(rental?.rentalEndDatetime)
            )}{" "}
            <span className="font-medium text-[10px]">Hours</span>
          </text-[9px] p>
          <text- className=" flex flex-col gap-1 text-[#656575] font-Inter text-xs font-bold  ">
            ${rental?.escrows?.depositAmount}{" "}
            <span className="font-medium text-[10px]">Total</span>
          </text-[9px] p>
        </div> */}
      </div>
      <p className=" font-Inter text-lg text-black font-medium pt-2 border-t border-greyText ">
        {rental?.car.make} {rental?.car.model}
      </p>
      {showEndTripModal && ( // <div className="fixed top-0 left-0 w-full h-full bg-[rgba(0,0,0,0.76)] z-[99999999] flex justify-center items-center">
        //   {/* Modal */}
        //   <div className="bg-[#F6F7F9] mx-auto my-10   w-[80%] lg:w-[866px]  flex flex-col gap-5 py-5 border rounded-2xl max-h-[90%] overflow-y-scroll ">
        //     <div className=" mx-auto w-[80%]">{step}</div>
        //   </div>
        // </div>
        <div>{step}</div>
      )}

      {showReview && (
        <div className="fixed z-[9999999] inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div
              // ref={endTrip}
              className="inline-block align-middle w-full rounded-[25px] bg-white p-6  text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
            >
              {!isReviewSubmitted ? (
                <>
                  <div className="flex flex-col gap-3 items-center">
                    <h1 className=" font-Poppins text-[28px] leading-[51px] font-bold text-[#261e27]">
                      Drop a review for the Lessor
                    </h1>
                    {overallRating > 0 && (
                      <div className="flex flex-col gap-3 items-center">
                        <h1 className=" font-Poppins text-[30px] font-medium leading-[51px] text-[#261e27]">
                          Overall Rating
                        </h1>
                        <div className="flex gap-3 items-center font-Poppins text-[20px] font-medium text-[#8E9297] ">
                          {overallRating}
                          <FaStar fill="#FABF35" className=" h-5 w-5" />
                        </div>
                      </div>
                    )}
                    <p className=" font-Poppins font-semibold text-[25px] text-[#76177]">
                      Rate this categories
                    </p>
                  </div>
                  <div className="flex flex-col gap-5 p-6">
                    <ReviewCategory
                      title="Cleanliness"
                      rating={cleanliness}
                      setRating={setCleanliness}
                    />
                    <ReviewCategory
                      title="Accuracy"
                      rating={accuracy}
                      setRating={setAccuracy}
                    />
                    <ReviewCategory
                      title="Communication"
                      rating={communication}
                      setRating={setCommunication}
                    />
                    <ReviewCategory
                      title="Maintenance"
                      rating={maintenance}
                      setRating={setMaintenance}
                    />
                  </div>
                  <textarea
                    placeholder="Enter a review"
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    className=" min-h-44 w-full outline-none border border-darkGreen rounded-md text-[#908C91] text-[22px] leading-9 font-Poppins p-3 font-normal"
                  />
                  <div className="w-full flex justify-center ">
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          !cleanliness ||
                          !accuracy ||
                          !communication ||
                          !maintenance ||
                          !reviewText
                        ) {
                          return showToast("Please fill all fields", "error");
                        }
                        createCarRating({
                          variables: {
                            data: {
                              accuracy,
                              cleanliness,
                              communication,
                              maintenance,
                              meanRating: overallRating,
                              comment: reviewText,
                              ratedBy: {
                                id: user?.id,
                              },
                              car: {
                                id: rental.car.id,
                              },
                            },
                          },
                        });
                      }}
                      className=" mx-auto py-3 px-8 w-fit text-white font-Poppins font-black text-[22px] leading-9 bg-midGreen border-none rounded-lg"
                    >
                      Submit
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-[83px] justify-center items-center ">
                  <div className="flex flex-col gap-[60px] justify-center items-center ">
                    <img
                      src={Tick}
                      alt=""
                      className="h-[143px] w-[143px] mx-auto"
                    />
                    <p className=" font-Poppins font-medium text-[20px] leading-[110%] text-midGreen uppercase tracking-[13%]">
                      Thank you for leaving a review
                    </p>
                  </div>
                  <div className="w-full flex justify-center ">
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/dashboard");
                      }}
                      className=" mx-auto py-3 px-8 w-fit text-white font-Poppins font-black text-[22px] leading-9 bg-midGreen border-none rounded-lg"
                    >
                      View Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalCard;
